import { Button, FlexBox, Typography } from '@vp/swan';
import React, { useContext, useEffect } from 'react';
import { CAContext } from '../../contexts/CAContext';
import { ChatbotContext } from '../../contexts/ChatbotContext';
import { MiawChatContext } from '../../contexts/MiawChatContext';
import EndChatFeedback from './EndChatFeedback';
import { useCustomTracking } from '../../hooks/custom-hooks/useTracking';
import { useIdentity } from '@vp/auth-react';
import { IIdentityObject } from '../../interfaces/app-interfaces';
import { useChatFooterWebSocket } from '../../hooks/chatbot/useChatFooterWebSocket';

export interface IEndChatPageProps {
  onCancelEnd?: () => void;
  onEndChatFlowComplete?: () => void;
}
const EndChatPage: React.FC<IEndChatPageProps> = ({ onCancelEnd, onEndChatFlowComplete }) => {
  const {
    chatState: { chatSession, enableChatbot, endChatInfo },
    setChatState,
  } = useContext(CAContext);
  const {
    state: { sessionId, endChatbotSession, endChatData: chatbotEndChatData, endChatLoading: chatbotEndLoading, isChatbotEndError },
  } = useContext(ChatbotContext);
  const {
    state: { conversationId, endMiawChatSession, endChatData: miawEndChatData, endChatLoading: miawEndChatLoading, isMIAWEndChatError },
    setState: setMIAWState,
  } = useContext(MiawChatContext);

  const webSocket = useChatFooterWebSocket({ conversationId: conversationId || '', isrequestConnection: false });
  const closeConnection = webSocket?.closeConnection;

  const { sendTrackingEvent } = useCustomTracking();

  const identity = useIdentity();
  const { isSignedIn } = identity as IIdentityObject;

  const [showFeedbackPage, setShowFeedbackPage] = React.useState(false);

  /** Set endChatInfo in chatState when component mounts */
  // This is used to track the session type and ID when ending the chat
  useEffect(() => {
    setChatState((prevState) => ({
      ...prevState,
      endChatInfo:
        chatSession === 'chatbotSession'
          ? { sessionType: 'chatbot', sessionId: sessionId }
          : chatSession === 'miawChatSession'
            ? { sessionType: 'miawChat', sessionId: conversationId }
            : undefined,
    }));
  }, []);

  const onCloseChat = () => {
    // Removing userData context on end chat so that email can be asked again in prechat form
    if (!isSignedIn) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setChatState((prevState: any) => ({
        ...prevState,
        userInfo: {
          ...prevState.userInfo,
          userData: null,
        },
      }));
    }

    setMIAWState((prev) => ({
      ...prev,
      attachmentErrorMessage: '',
      showAttachmentError: false,
    }));

    if (chatSession === 'miawChatSession') {
      console.log('Closing MIAW chat session', endMiawChatSession);
      closeConnection('Chat ended by user');
      endMiawChatSession?.();
    } else if (chatSession === 'chatbotSession') {
      console.log('Closing Chatbot session', endChatbotSession);
      endChatbotSession?.({ escalate: false });
    }
  };

  /** once the chat end data is available, reset chat state and show feedback page */
  useEffect(() => {
    if (miawEndChatData || chatbotEndChatData || isChatbotEndError || isMIAWEndChatError) {
      // Reset chat state and show feedback page
      setChatState((prevState) => ({
        ...prevState,
        chatSession: 'endChatSession',
        isPrivacyPolicyAccepted: false,
      }));
      setShowFeedbackPage(true);
    }
  }, [miawEndChatData, chatbotEndChatData, isChatbotEndError, isMIAWEndChatError]);

  const onStartNewChat = () => {
    setChatState((prevState) => ({
      ...prevState,
      endChatInfo: undefined,
    }));
    onEndChatFlowComplete?.();
    // startChatbotSession?.();
  };

  useEffect(() => {
    if (showFeedbackPage && enableChatbot && endChatInfo?.sessionType === 'chatbot') {
      sendTrackingEvent('Chatbot Viewed', 'Chatbot:Feedback', 'endChatFeedbackPromptShown');
    }
  }, [showFeedbackPage, endChatInfo, enableChatbot]);

  if (showFeedbackPage) {
    return <EndChatFeedback onStartNewChat={onStartNewChat} />;
  }
  return (
    <FlexBox compactMode flexDirection={'column'} alignItems={'center'} justifyContent={'center'} style={{ height: '100%' }}>
      <Typography fontSkin="title-section" mb="7" textAlign={'center'}>
        Are you sure you want to end the conversation?
      </Typography>
      <FlexBox flexDirection="column" justifyContent={'space-between'} gap={'between-actions'}>
        <Button skin="primary" onClick={onCloseChat} loading={chatbotEndLoading || miawEndChatLoading}>
          Yes, end conversation
        </Button>
        <Button skin="secondary" onClick={onCancelEnd} disabled={chatbotEndLoading || miawEndChatLoading}>
          No, continue chatting
        </Button>
      </FlexBox>
    </FlexBox>
  );
};

export default EndChatPage;
